import React, { useEffect, useState } from 'react';
import PageContainer from '../../../components/PageContainer';
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, FormControlLabel, Grid, IconButton } from '@mui/material';
import { BusinessOutlined, CloudDownloadOutlined, DeleteOutlined, DownloadOutlined, EditOutlined } from '@mui/icons-material';
import { Link, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment';
import { addOrUpdateSanction, deleteSanction, getAllInstitutions, getAllSanctions, getAllSanctionsDT } from '../../../redux/api/institution';
import { useSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import TextInput from '../../../components/common/TextInput';
import SelectInput from '../../../components/common/SelectInput';
import DocumentInput from '../../../components/common/DocumentInput';
import { getDocument } from '../../../redux/api/workflows';
import { currencyFormat, sanctionColumns } from '../../../util';
import Filters from '../../../components/Filters';

const Sanctions = () => {
    const [processing, setProcessing] = useState(false);
    const [performingAction, setPerformingAction] = useState(false);
    const defaultSanction = {
        id: '',
        title: '',
        amount: '',
        isPaid: false,
        institutionId: '',
        documentId: '',
        attachmentDocument: '',
        description: '',
        institutionName: '',
        documentFileName: '',
        documentFileSize: '',
    };
    const [actionModal, setActionModal] = useState(false);
    const [deleteModal, showDeleteModal] = useState(false);
    const [selectedSanction, setSelectedSanction] = useState(defaultSanction); // activate || deactivate
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const [records, setRecords] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [existingSearch, setExistingSearch] = useState({});

    const columns = [
        {
            field: 'sn',
            headerName: 'S/N',
            width: 80,
            sortable: false,
            renderCell: (params) => {
                return <p style={{ margin: 'auto' }}>{(params?.api?.getRowIndex(params?.row?.id) || 0) + 1}</p>;
            },
        },
        {
            field: 'action',
            headerName: 'Quick Action',
            width: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <IconButton
                            size="small"
                            color="primary"
                            variant="outlined"
                            title="Edit"
                            onClick={() => {
                                setSelectedSanction(params?.row);
                                setActionModal(true);
                            }}
                        >
                            <EditOutlined />
                        </IconButton>
                        <IconButton
                            size="small"
                            color="info"
                            variant="outlined"
                            title="Download Attached Document"
                            onClick={async () => {
                                try {
                                    await getDocument(params?.row?.documentId, params?.row?.documentFileName);
                                } catch (err) {
                                    enqueueSnackbar(err?.message || 'An error occurred', { variant: 'error' });
                                }
                            }}
                        >
                            <DownloadOutlined />
                        </IconButton>
                        <IconButton
                            size="small"
                            color="error"
                            variant="outlined"
                            title="Delete Sanction"
                            onClick={() => {
                                setSelectedSanction(params?.row);
                                showDeleteModal(true);
                            }}
                        >
                            <DeleteOutlined />
                        </IconButton>
                    </div>
                );
            },
        },
        { field: 'title', headerName: 'Title', width: 200 },
        { field: 'description', headerName: 'Description', width: 300 },

        { field: 'institutionName', headerName: 'Institution', width: 350 },
        {
            field: 'amount',
            headerName: 'Amount',
            width: 350,
            valueGetter: (params) => currencyFormat.format(params?.row?.amount),
        },
        { field: 'isPaid', headerName: 'Paid', width: 350 },
        {
            field: 'createdOn',
            headerName: 'Creation Date',
            width: 200,
            valueGetter: (params) => (params?.row?.createdOn ? moment(params?.row?.createdOn).format('DD-MM-YYYY hh:mm A') : ''),
        },
        // { field: 'createdDate', headerName: 'Creation Date', width: 200, valueGetter: (params) => params?.row?.createdOn ? (moment(params?.row?.createdOn).format('DD/MM/YY hh:mm')) : '' },
        // { field: 'createdBy', headerName: 'Creation By', width: 200 }
    ];

    const performAction = async (values) => {
        try {
            setPerformingAction(true);
            const formData = new FormData();
            Object.keys(values).forEach((key) => {
                formData.append(key, values[key]);
            });
            await addOrUpdateSanction(formData);
            enqueueSnackbar(`Sanction ${values?.id ? 'edited' : 'added'} successfully`);
            init();
            setActionModal(null);
            setSelectedSanction(defaultSanction);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setPerformingAction(false);
        }
    };

    const deleteSanctionData = async (values) => {
        try {
            setPerformingAction(true);
            await deleteSanction(values?.id);
            enqueueSnackbar(`Sanction Deleted successfully`);
            init();
            showDeleteModal(null);
            setSelectedSanction(defaultSanction);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setPerformingAction(false);
        }
    };

    const init = async () => {
        try {
            setProcessing(true);
            const res = await getAllSanctionsDT();
            const institutions = await getAllInstitutions();
            setRecords(res?.resultObject?.data || []);
            setInstitutions(institutions?.resultObject || []);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const searchFunction = async (values, closeSearchModal) => {
        try {
            const res = await getAllSanctionsDT(values);
            setRecords(res?.resultObject?.data || []);
            setExistingSearch(values);
            closeSearchModal();
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    const exportData = async () => {
        try {
            setExporting(true);
            const resp = await getAllSanctionsDT(existingSearch, true);
            enqueueSnackbar(resp?.message || 'Your report is being processed, kindly check the reports page for progress');
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <PageContainer
            pageTitle="All Sanctions"
            tagline="Manage all sanctions"
            processing={processing}
            buttonComponent={
                <>
                    <Button
                        onClick={() => {
                            setSelectedSanction(defaultSanction);
                            setActionModal(true);
                        }}
                        startIcon={<BusinessOutlined />}
                        style={{ margin: '0 .5rem' }}
                        variant="contained"
                    >
                        Create Sanction
                    </Button>
                </>
            }
        >
            <Filters searchFields={[]} searchFunction={searchFunction} advancedSearchFields columns={sanctionColumns} showFilter={false} />

            <Box className="">
                <div className="paper" style={{ height: 550, width: '100%', textAlign: 'center' }}>
                    {records?.length > 0 && (
                        <div className="export-btn">
                            <Button onClick={exportData} variant="outlined" startIcon={<CloudDownloadOutlined />}>
                                {exporting ? <CircularProgress color="inherit" size={15} /> : 'Export'}
                            </Button>
                        </div>
                    )}

                    <DataGrid
                        className={'center-first-column'}
                        rows={records}
                        getRowId={(row) => row.id}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        checkboxSelection={false}
                        disableSelectionOnClick
                        style={{ fontSize: '12px' }}
                    />
                </div>
            </Box>

            <Dialog open={Boolean(actionModal)}>
                <h4 className="text-center mt-2">{Boolean(selectedSanction?.id) ? 'Edit' : 'Create'} Sanction</h4>
                <Formik initialValues={{ ...selectedSanction }} enableReinitialize onSubmit={performAction}>
                    {({ isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <DialogContent>
                                <Grid container spacing={3} paddingBottom={3}>
                                    <Grid item xs={12}>
                                        <TextInput id="title" required label="Title" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextInput id="description" multiline minRows={2} required label="Description" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <SelectInput id="institutionId" options={institutions} required field="institutionId" fieldDisplay="name" minRows={2} label="Institution" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextInput id="amount" type="number" required label="Amount" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={<Checkbox checked={values.isPaid} id={'isPaid'} onChange={(ev, value) => setFieldValue('isPaid', value)} name={'is Paid'} />}
                                            label={'Paid'}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DocumentInput
                                            id="attachmentDocument"
                                            value={values.documentFileName}
                                            viewFunction={async () => {
                                                await getDocument(values?.documentId, values?.documentFileName);
                                            }}
                                            required
                                            label="Document"
                                        />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Button
                                            disabled={isSubmitting}
                                            fullWidth
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => {
                                                if (!performingAction) {
                                                    setActionModal(false);
                                                    setSelectedSanction(defaultSanction);
                                                }
                                            }}
                                            style={{ marginTop: '.5rem' }}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button disabled={isSubmitting} fullWidth variant="contained" type="submit" style={{ marginTop: '.5rem' }}>
                                            {isSubmitting ? <CircularProgress size={15} color="inherit" /> : values?.id ? 'Edit Sanction' : 'Add Sanction'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            <Dialog
                open={Boolean(deleteModal)}
                onClose={() => {
                    if (!performingAction) {
                        showDeleteModal(false);
                        setSelectedSanction(defaultSanction);
                    }
                }}
            >
                <h4 className="text-center mt-2 mb-0">Confirm Sanction Deletion</h4>
                <Formik initialValues={{ ...selectedSanction }} enableReinitialize onSubmit={deleteSanctionData}>
                    {({ isSubmitting, values }) => (
                        <Form>
                            <DialogContent>
                                <Grid container spacing={3} paddingBottom={3}>
                                    <Grid item xs={12}>
                                        Kindly confirm you want to delete sanction with details below:
                                        <br />
                                        <br />
                                        {/* <b>ID:</b> {values?.id} <br /> */}
                                        <b>Title:</b> {values?.title} <br />
                                        <b>Description:</b> {values?.description} <br />
                                        <b>Institution:</b> {values?.institutionName}
                                        <br />
                                        <b>Amount:</b> NGN {currencyFormat.format(values?.amount)} <br />
                                        <b>Paid?:</b> {values?.isPaid}
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Button
                                            disabled={isSubmitting}
                                            fullWidth
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => {
                                                if (!performingAction) {
                                                    showDeleteModal(false);
                                                    setSelectedSanction(defaultSanction);
                                                }
                                            }}
                                            style={{ marginTop: '.5rem' }}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button disabled={isSubmitting} startIcon={<DeleteOutlined />} color="error" fullWidth variant="contained" type="submit" style={{ marginTop: '.5rem' }}>
                                            {isSubmitting ? <CircularProgress size={15} color="inherit" /> : 'Delete Sanction'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </PageContainer>
    );
};

export default Sanctions;
